import { Component, ElementRef, HostListener, ViewChild, computed, effect, inject, input, output, signal } from '@angular/core';
import { Auth, authState, signOut } from '@angular/fire/auth';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  PLATFORM_NOTIFICATION_EVENTS,
  PlatformNotification,
  PlatformNotificationEventType,
  PlatformNotificationSent,
  platformEventLabel,
} from '../platform-notifications/platform-notification.model';
import { PlatformNotificationsService } from '../platform-notifications/platform-notifications.service';

/**
 * Initials for the avatar chip: first letters of the first two words of the
 * display name, else the first letter of the email, else "?".
 */
export function initialsFor(displayName: string | null | undefined, email: string | null | undefined): string {
  const words = (displayName || '').trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  if (words.length === 1) return words[0][0].toUpperCase();
  const e = (email || '').trim();
  return e ? e[0].toUpperCase() : '?';
}

/** Short relative time for the recent-alerts list. */
export function relativeTimeFrom(value: any, now = Date.now()): string {
  const ms = value?.toMillis?.() ?? (typeof value?.seconds === 'number' ? value.seconds * 1000 : new Date(value).getTime());
  if (!ms || isNaN(ms)) return '';
  const diff = Math.max(0, now - ms);
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return d < 30 ? `${d}d ago` : new Date(ms).toLocaleDateString();
}

/**
 * Super Admin User panel — opened from the avatar in the top nav via
 * SuperAdminUserDialogService, hosted by MainComponent. Left column: who is
 * signed in, sign-out. Right column: platform notifications — one toggle per
 * catalog event, each writing straight away (spinner while in flight, rolled
 * back with an inline error on failure), plus the user's recent alerts.
 * Modal mechanics (backdrop, Escape, focus hand-back) mirror
 * ConfirmationDialogComponent; the backdrop is z-[60] so it covers the fixed
 * nav (z-50) and the FABs (z-40).
 */
@Component({
  selector: 'app-super-admin-user-dialog',
  imports: [],
  templateUrl: './super-admin-user-dialog.component.html',
  styleUrl: './super-admin-user-dialog.component.css',
})
export class SuperAdminUserDialogComponent {
  private auth = inject(Auth);
  private notifications = inject(PlatformNotificationsService);

  readonly isOpen = input(false);
  readonly close = output<void>();

  @ViewChild('dialogPanel') dialogPanel?: ElementRef<HTMLElement>;

  readonly user = toSignal(authState(this.auth), { initialValue: this.auth.currentUser });
  readonly environmentName = environment.name;
  readonly isProduction = environment.name === 'production';
  isSigningOut = false;

  // ---- Platform notifications ----
  readonly events = PLATFORM_NOTIFICATION_EVENTS;
  private readonly uid = computed(() => this.user()?.uid ?? null);
  private readonly open = signal(false);

  /** Live rows for the signed-in user; only listened to while the panel is open. */
  private readonly rows = toSignal(
    this.streamWhileOpen((uid) => this.notifications.getRows(uid)),
    { initialValue: null as PlatformNotification[] | null },
  );
  readonly recentSent = toSignal(
    this.streamWhileOpen((uid) => this.notifications.getRecentSent(uid)),
    { initialValue: null as PlatformNotificationSent[] | null },
  );
  readonly rowsLoading = computed(() => this.open() && this.rows() === null);

  /** Optimistic state per event while its write is in flight. */
  private readonly optimistic = signal<Partial<Record<PlatformNotificationEventType, boolean>>>({});
  private readonly pending = signal<Set<PlatformNotificationEventType>>(new Set());
  toggleError = signal<string | null>(null);

  private previouslyFocused: HTMLElement | null = null;

  constructor() {
    effect(() => {
      const isOpen = this.isOpen();
      this.open.set(isOpen);
      if (isOpen) {
        this.toggleError.set(null);
        this.previouslyFocused = document.activeElement as HTMLElement | null;
        setTimeout(() => this.dialogPanel?.nativeElement.querySelector<HTMLElement>('[data-dialog-close]')?.focus());
      } else if (this.previouslyFocused) {
        if (this.previouslyFocused.isConnected) this.previouslyFocused.focus();
        this.previouslyFocused = null;
      }
    });
  }

  /** Bridges (open, uid) into a Firestore stream that is torn down whenever the panel closes. */
  private streamWhileOpen<T>(factory: (uid: string) => Observable<T>): Observable<T | null> {
    return toObservable(computed(() => (this.open() ? this.uid() : null))).pipe(
      switchMap((uid) => (uid ? factory(uid) : of(null))),
    );
  }

  initials(): string {
    const u = this.user();
    return initialsFor(u?.displayName, u?.email);
  }

  isEnabled(eventType: PlatformNotificationEventType): boolean {
    const optimistic = this.optimistic()[eventType];
    if (optimistic !== undefined) return optimistic;
    return this.rows()?.find((r) => r.eventType === eventType)?.enabled === true;
  }

  isPending(eventType: PlatformNotificationEventType): boolean {
    return this.pending().has(eventType);
  }

  /** The address alerts go to: the most recently saved row's, else the account email. */
  deliveryEmail(): string {
    const rows = this.rows() || [];
    const latest = [...rows].sort((a, b) => (b.updatedAt?.seconds ?? 0) - (a.updatedAt?.seconds ?? 0))[0];
    return latest?.email || this.user()?.email || '';
  }

  async toggle(eventType: PlatformNotificationEventType): Promise<void> {
    const u = this.user();
    if (!u?.email || this.rowsLoading() || this.isPending(eventType)) return;

    const enabled = !this.isEnabled(eventType);
    const create = !this.rows()?.some((r) => r.eventType === eventType);
    const label = platformEventLabel(eventType);

    this.toggleError.set(null);
    this.optimistic.update((s) => ({ ...s, [eventType]: enabled }));
    this.pending.update((s) => new Set(s).add(eventType));
    try {
      await this.notifications.setEventEnabled(
        { uid: u.uid, email: u.email, displayName: u.displayName ?? null },
        eventType,
        enabled,
        create,
      );
    } catch (err) {
      console.error('Failed to update platform notification setting:', err);
      this.toggleError.set(`Couldn't update "${label}". Please try again.`);
    } finally {
      this.pending.update((s) => {
        const next = new Set(s);
        next.delete(eventType);
        return next;
      });
      this.optimistic.update((s) => {
        const next = { ...s };
        delete next[eventType];
        return next;
      });
    }
  }

  eventLabel = platformEventLabel;
  relativeTime = relativeTimeFrom;

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen()) this.onClose();
  }

  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) this.onClose();
  }

  onClose(): void {
    if (!this.isSigningOut) this.close.emit();
  }

  // AppComponent listens to authState and swaps to the login screen on null,
  // which tears this dialog down along with the rest of the shell.
  async signOut(): Promise<void> {
    if (this.isSigningOut) return;
    this.isSigningOut = true;
    try {
      await signOut(this.auth);
    } finally {
      this.isSigningOut = false;
    }
  }
}
